import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Download, MessageCircle } from "lucide-react";
import { useContent } from "../content-context";
import { scrollToSection } from "../lib/scroll";
import { Eyebrow, Reveal } from "./Reveal";

const POSTER_PATH = "/images/real/traitors-poster-mystery-begins.webp";

async function sharePosterToWhatsApp() {
  const text = "Traitors Island — can you be trusted? An immersive mystery weekend on our own private island: Collanmore Island Lodge, Clew Bay, Westport.";
  try {
    const res = await fetch(POSTER_PATH);
    const blob = await res.blob();
    const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
    const file = new File([blob], `Traitors-Island-Poster.${ext}`, { type: blob.type || "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Traitors Island", text });
      return;
    }
  } catch (err) {
    if (err && err.name === "AbortError") return;
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${text} Poster: ${window.location.origin}${POSTER_PATH}`)}`, "_blank", "noopener,noreferrer");
}

export default function Traitors() {
  const { traitors } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const posterY = useTransform(scrollYProgress, [0, 1], ["3%", "-3%"]);

  return (
    <section
      id="traitors-island"
      ref={ref}
      data-testid="traitors-section"
      className="letterbox relative overflow-hidden bg-navy py-24 md:py-32"
    >
      <div className="mx-auto max-w-3xl px-5 text-center md:px-10">
        <Reveal>
          <div className="flex justify-center">
            <Eyebrow light>{traitors.eyebrow}</Eyebrow>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <motion.img
            src={traitors.poster}
            alt={traitors.posterAlt}
            style={{ y: posterY }}
            loading="lazy"
            className="mt-10 w-full border border-gold/30 shadow-[0_40px_100px_rgba(0,0,0,0.65)]"
            data-testid="traitors-poster"
          />
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="traitors-poster-actions">
            <button
              type="button"
              onClick={sharePosterToWhatsApp}
              data-testid="traitors-poster-whatsapp"
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-2/90 transition-colors duration-300 hover:text-gold"
            >
              <MessageCircle className="h-4 w-4" />
              Send Poster on WhatsApp
            </button>
            <a
              href={POSTER_PATH}
              download="Traitors-Island-Poster.png"
              data-testid="traitors-poster-download"
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-2/90 transition-colors duration-300 hover:text-gold"
            >
              <Download className="h-4 w-4" />
              Download the Poster
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="mt-12 font-display text-3xl font-medium italic text-ivory md:text-5xl" data-testid="traitors-heading">
            {traitors.heading}
          </h2>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mx-auto mt-6 max-w-xl text-sm font-light leading-relaxed text-ivory/80 md:text-base" data-testid="traitors-copy">
            {traitors.copy}
          </p>
        </Reveal>
        <Reveal delay={0.32}>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollToSection("#contact"); }}
            data-testid="traitors-cta"
            className="group mt-10 inline-flex items-center gap-3 border border-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold-2 transition-all duration-500 hover:bg-gold hover:text-navy"
          >
            {traitors.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
